import {Box, Button, Stack, Typography} from '@mui/material';
import Cookies from 'js-cookie';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed, MainFooter, StyledGrid} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {RequestInfo} from 'presentation/pages/private/refund/components';
import {InvoiceTypeEnum} from 'presentation/pages/private/refund/constants/invoiceTypeEnum';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {formatPriceRefund, formatText} from 'presentation/utils';
import React, {Fragment, useState} from 'react';
import {useAccordionExpandedStore} from '../../store/useAccordionExpandedStore';
import {useStepStore} from '../../store/useStepStore';
import {useSubmitStatusStore} from '../../store/useSubmitStatusStore';
import {useTreatmentDataStore} from '../../store/useTreatmentDataStore';
import {AccordionExpandedEnum, StepEnum, SubmitStatusEnum} from '../../types';
import {BankInfoCard} from './components/BankInfoCard';
import {BeneficiaryInfoCard} from './components/BeneficiaryInfoCard';
import {CustomContainer, StyledFooterContainer} from './styles';
import {ConferenceProps} from './types';

const {totalReceiptValue, finishRequest, continueForNewRequest, confirmModalityModal} =
	translations['pt-br'].privateFlow.refund.conferencePage;
const {requestsLabel} = translations['pt-br'].commons;

export const Conference = ({handleNextStep, isLoading}: ConferenceProps) => {
	const [openModalityModalState, setOpenModalityModalState] = useState(false);
	const {isMobile, isLaptop} = useMediaQueries();
	const {handleCreateNewRequestInProtocol, setSelectedRequestIndex} = useRefundRequest();

	const treatmentDataState = useTreatmentDataStore(state => state.data);

	const {requests, handleResetStates, listRequestForms} = useRefundRequest();
	const setStep = useStepStore(state => state.setStep);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);

	const setSubmitStatus = useSubmitStatusStore(state => state.setStatus);

	const totalValue: number =
		requests.requestData?.reduce(
			(acc, item) => acc + parseFloat(item.financialData.invoiceAmount.replace(',', '.')),
			0,
		) || 0;

	const handleAddNewRequest = () => {
		handleCreateNewRequestInProtocol();
		setSubmitStatus(SubmitStatusEnum.ADD_NEW_REQUEST_IN_PROTOCOL);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setOpenModalityModalState(false);
		handleResetStates();
		Cookies.remove('uploadFilesTerm');
	};

	const handleEditProviderData = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.PROVIDER_DATA);
	};

	const handleEditPatientData = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.PATIENT_DATA);
	};

	const handleEditFiscalDocument = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.RECEIPT_REGISTRATION);
		setAccordionExpanded(AccordionExpandedEnum.FISCAL_DOCUMENT);
	};

	const handleEditAttachment = (index: number) => {
		setSelectedRequestIndex(index);
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		setStep(StepEnum.SEND_DOCUMENTS);
	};

	const handleConfirmModality = () => {
		setOpenModalityModalState(true);
	};

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root spacing={3}>
					<StyledGrid.Item md={12} lg={8}>
						<Stack spacing={2}>
							{requests.requestData ? (
								<Stack spacing={2}>
									<Typography variant="subtitle2" color="text.secondary">
										{requestsLabel}
									</Typography>
									<Stack spacing={2}>
										{requests.requestData.map((item, index) => {
											const attachments = listRequestForms[index].uploads;

											const isCNPJ =
												item.professionalData.professionalType === 'J';

											const showRequestNumber =
												requests.requestData.length >= 2;

											return (
												<Box key={item.requestCode}>
													<RequestInfo
														isSingleItem={
															requests.requestData.length === 1
														}
														city={formatText(
															item.financialData.invoiceIssueCity,
														)}
														state={item.financialData.invoiceIssueState}
														requestNumber={
															showRequestNumber
																? index + 1
																: undefined
														}
														patientName={
															formatText(item.beneficiaryName) || ''
														}
														doctorName={formatText(
															item.professionalData.professionalName,
														)}
														providerName={
															isCNPJ
																? formatText(
																		item.professionalData
																			?.providerName,
																	)
																: '-'
														}
														specialtyName={formatText(
															item.treatmentData.specialty
																.description,
														)}
														date={item.treatmentData.date.replaceAll(
															'-',
															'/',
														)}
														numFiscalDocument={String(
															item.financialData.invoiceNumber,
														)}
														numFiscalDigit={
															item.financialData.invoiceDigit
														}
														value={formatPriceRefund(
															item.financialData.invoiceAmount,
														)}
														hasNfe={
															item.financialData.invoiceType ===
															InvoiceTypeEnum.EMIT
														}
														attachments={attachments}
														handleClickProviderData={() =>
															handleEditProviderData(index)
														}
														handleClickPatientData={() =>
															handleEditPatientData(index)
														}
														handleClickFiscalDocument={() =>
															handleEditFiscalDocument(index)
														}
														handleClickAttachment={() =>
															handleEditAttachment(index)
														}
													/>
												</Box>
											);
										})}
									</Stack>
								</Stack>
							) : null}

							<BankInfoCard />

							{isLaptop ? <BeneficiaryInfoCard /> : null}

							<CTAButtonFixed.Root justifyContent="space-between">
								<Stack
									direction="row"
									flex={1}
									gap={1}
									alignItems="center"
									justifyContent={isMobile ? 'space-between' : 'flex-end'}>
									{requests.requestData ? (
										<Fragment>
											<Typography variant="subtitle1" color="text.primary">
												{totalReceiptValue}
											</Typography>
											<Typography variant="h5" color="text.primary">
												{new Intl.NumberFormat('pt-Br', {
													style: 'currency',
													currency: 'BRL',
												}).format(totalValue)}
											</Typography>
										</Fragment>
									) : null}
								</Stack>

								<Stack
									flex={0}
									gap={1}
									justifyContent="flex-end"
									flexDirection={isMobile ? 'column' : 'row'}>
									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_CADASTRAR_OUTRA_SOLICITACAO
										}
										size="large"
										variant="outlined"
										onClick={handleConfirmModality}
										fullWidth={isMobile}>
										{continueForNewRequest}
									</CTAButtonFixed.Action>

									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_FINALIZAR_SOLICITACAO_REEMBOLSO
										}
										disabled={isLoading}
										size="large"
										onClick={handleNextStep}
										variant="contained"
										fullWidth={isMobile}
										endIcon={
											isLoading ? <Loading size={20} color="inherit" /> : null
										}>
										{finishRequest}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>
					{!isLaptop ? (
						<StyledGrid.Item md={12} lg={4}>
							<Stack spacing={2}>
								<BeneficiaryInfoCard />
							</Stack>
						</StyledGrid.Item>
					) : null}
				</StyledGrid.Root>
			</CustomContainer>

			<AlertModal
				headerDataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_TIPO_ATENDIMENTO}
				closeBtnDataTagId={RefundTaggingEnum.CLICK_FECHAR_MODAL_TIPO_ATENDIMENTO}
				variant="info"
				title={confirmModalityModal.title}
				open={openModalityModalState}
				onClose={() => setOpenModalityModalState(false)}
				headerTitle={confirmModalityModal.headerTitle}
				subDescription={
					<Stack gap={0} textAlign="center">
						<Typography>
							{confirmModalityModal.description1}
							<strong>{treatmentDataState.modality.toLowerCase()}</strong>.
						</Typography>
						<Typography>{confirmModalityModal.description2}</Typography>
					</Stack>
				}
				footerContent={
					<Stack gap={1} sx={{width: '100%'}}>
						<Button
							data-tag-id={RefundTaggingEnum.CLICK_VOLTAR_PARA_REVISAO_REEMBOLSO}
							onClick={() => setOpenModalityModalState(false)}
							variant="outlined"
							size="large"
							fullWidth>
							{confirmModalityModal.button1}
						</Button>
						<Button
							data-tag-id={
								RefundTaggingEnum.CLICK_PROSSEGUIR_NOVA_SOLICITACAO_REEMBOLSO
							}
							onClick={handleAddNewRequest}
							variant="contained"
							size="large"
							fullWidth>
							{confirmModalityModal.button2}
						</Button>
					</Stack>
				}
			/>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
};
